import { z, type ZodTypeAny } from "zod";

// Settings-Form-Engine (§3.4/D12: „Schema = Single Source of Truth"). Liest ein Zod-
// Settings-Schema introspektiv aus → Feld-Deskriptoren + Default-Seed. Treibt den
// generischen Fallback (SchemaForm) UND seedt die Defaults für die handgebauten
// SettingsUIs — beides aus derselben Quelle, kein paralleler Default-Satz.
//
// ENTKOPPELT von Zod-Internals: ausschließlich über die ÖFFENTLICHE, GETYPTE Zod-API
// (instanceof + Getter wie .shape/.minValue/.options/.unwrap()/.innerType()/
// .removeDefault() + safeParse für Default-Werte). KEIN `(x)._def.typeName`-String-Zugriff
// mehr. Das macht die Engine NICHT automatisch Zod-v4-fest — v4 benennt Klassen um (z. B.
// ZodEffects → ZodPipe/Transform), `z.ZodEffects` wäre dort `undefined`. Der Gewinn ist die
// SICHTBARKEIT: ein v4-Wechsel bricht hier als TS-Compile-Fehler / TypeError an genau den
// geänderten Stellen, statt über einen `as any`-Cast still falsch zu rendern. Die Smoke-Tests
// (test/introspect.test.ts) pinnen das Verhalten an echten + synthetischen Schemas. (`zod/v4`
// ist via zod@3.25 mitinstalliert; aktiv genutzt wird aber der v3-Entry `from "zod"`.)

export type FieldKind = "string" | "number" | "boolean" | "enum";

export interface SettingsField {
  key: string;
  kind: FieldKind;
  optional: boolean;
  default?: unknown;
  min?: number;
  max?: number;
  int?: boolean;
  options?: string[]; // enum
  /** Schrittweite (Slider/Stepper) — von der Editor-Details-Form (D70 number/range) gesetzt; Zod-Introspektion lässt es leer. */
  step?: number;
  /** Einheit-Suffix (z.B. "px/s") — analog nur aus der Details-Form. */
  unit?: string;
}

/** Default eines Feldes = sein ECHTES Laufzeit-Ergebnis bei fehlendem Input. `safeParse(undefined)`
 *  am GESAMTEN Feld lässt ZodDefault seinen Default füllen UND validiert durch alle äußeren
 *  Wrapper (inkl. `.refine()`): so kann nie ein gegen das Feld ungültiger Default-Seed entstehen
 *  (`.default(10).refine(n>20)` → kein Default). `.optional()` außen hebt den Default bewusst aus
 *  (Zod kurzschließt undefined → undefined); das spiegeln wir 1:1, statt es zu überschreiben.
 *  Liefert nur echte Defaults (kein `undefined`). */
function readDefault(field: ZodTypeAny): unknown {
  const r = field.safeParse(undefined);
  return r.success && r.data !== undefined ? r.data : undefined;
}

/** ZodEffects(refine/transform)/ZodOptional/ZodNullable/ZodDefault abschälen → {leaf, optional}.
 *  WICHTIG: `optional` heißt „Feld darf fehlen" — das gilt NUR für ZodOptional. ZodNullable
 *  (Wert darf `null` sein, muss aber präsent sein) wird abgeschält, setzt aber `optional`
 *  NICHT — sonst ließe defaultsOf ein nullable-Pflichtfeld aus und der Seed wäre unvollständig.
 *  Begrenzte Tiefe schützt vor pathologischen Schachtelungen. */
function unwrap(s: ZodTypeAny): { leaf: ZodTypeAny; optional: boolean } {
  let cur: ZodTypeAny = s;
  let optional = false;
  for (let i = 0; i < 12; i++) {
    if (cur instanceof z.ZodEffects) cur = cur.innerType();
    else if (cur instanceof z.ZodOptional) {
      optional = true;
      cur = cur.unwrap();
    } else if (cur instanceof z.ZodNullable) {
      cur = cur.unwrap(); // nullable ≠ optional
    } else if (cur instanceof z.ZodDefault) {
      cur = cur.removeDefault();
    } else break;
  }
  return { leaf: cur, optional };
}

/** Außen ZodEffects/Default/Optional/Nullable abschälen, bis ein ZodObject erreicht ist. */
function asObject(schema: ZodTypeAny): Record<string, ZodTypeAny> | undefined {
  let cur: ZodTypeAny = schema;
  for (let i = 0; i < 12; i++) {
    if (cur instanceof z.ZodObject) return cur.shape as Record<string, ZodTypeAny>;
    if (cur instanceof z.ZodEffects) cur = cur.innerType();
    else if (cur instanceof z.ZodDefault) cur = cur.removeDefault();
    else if (cur instanceof z.ZodOptional || cur instanceof z.ZodNullable) cur = cur.unwrap();
    else return undefined;
  }
  return undefined;
}

function leafKind(leaf: ZodTypeAny): { kind: FieldKind; options?: string[]; min?: number; max?: number; int?: boolean } {
  if (leaf instanceof z.ZodEnum) return { kind: "enum", options: [...(leaf.options as readonly string[])] };
  if (leaf instanceof z.ZodNumber) {
    return { kind: "number", min: leaf.minValue ?? undefined, max: leaf.maxValue ?? undefined, int: leaf.isInt };
  }
  if (leaf instanceof z.ZodBoolean) return { kind: "boolean" };
  return { kind: "string" };
}

/** Feld-Deskriptoren aus einem Settings-Schema (leeres Array, wenn kein ZodObject). */
export function introspectSettings(schema: ZodTypeAny): SettingsField[] {
  const shape = asObject(schema);
  if (!shape) return [];
  const fields: SettingsField[] = [];
  for (const [key, raw] of Object.entries(shape)) {
    const { leaf, optional } = unwrap(raw);
    const meta = leafKind(leaf);
    fields.push({ key, optional, default: readDefault(raw), ...meta });
  }
  return fields;
}

/** Default-Seed für die Form (Default-Wert; sonst sinnvoller Leerwert je Kind). */
export function defaultsOf(schema: ZodTypeAny): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of introspectSettings(schema)) {
    if (f.default !== undefined) out[f.key] = f.default;
    else if (f.optional) continue; // optional ohne Default → weglassen
    else if (f.kind === "string") out[f.key] = "";
    else if (f.kind === "number") out[f.key] = f.min ?? 0;
    else if (f.kind === "boolean") out[f.key] = false;
    else if (f.kind === "enum") out[f.key] = f.options?.[0] ?? "";
  }
  return out;
}
